import React from 'react';
import {
  SPACINGS,
  formatDate,
  useMediaQuery,
  Divider,
  FlexWrapper,
  documentTypeNames,
} from '../../utils';
import { useTranslate } from '@federico.moretti/react-translate';
import styled, { useTheme } from 'styled-components/macro';
import DetailsList from '../DetailsList';
import { Heading, Text } from '../../typography';
import FileButton from '../FileButton';
import { Card } from '../../atoms';
import { IconEye, IconWrapper } from '../../icons';

const ActionWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: ${SPACINGS[6]};
`;

type UserDocumentProps = {
  name?: string;
  number: string;
  type: string;
  issueDate?: string;
  issuedBy?: string;
  expirationDate?: string;
  country?: string;
  files?: { id: string; filename: string; action: () => void }[];
  action?: React.ReactNode;
  style?: React.CSSProperties;
};
function UserDocument(props: UserDocumentProps) {
  const {
    name,
    number,
    type,
    issueDate,
    issuedBy,
    expirationDate,
    country,
    files,
    action,
    style,
  } = props;
  const { t, language } = useTranslate();
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.mediaQueries.isMobile);

  const isTaxCode = type === documentTypeNames.taxCode;

  const items = React.useMemo(
    () => [
      { label: t('documentNumber'), value: number },
      { label: t('type'), value: type },
      !isTaxCode && {
        label: t('issueDate'),
        value: issueDate && formatDate(issueDate, language),
      },
      !isTaxCode && { label: t('issuedBy'), value: issuedBy },
      !isTaxCode && {
        label: t('expirationDate'),
        value: expirationDate && formatDate(expirationDate, language),
      },
      country ? { label: t('country'), value: country } : undefined,
      !isMobile
        ? {
            label: t('files'),
            value: files ? (
              <FlexWrapper justify="flex-start">
                {files.map((f, i) => (
                  <FileButton
                    key={i}
                    action={f.action}
                    style={{
                      marginRight: SPACINGS[2],
                      marginBottom: SPACINGS[2],
                    }}
                  >
                    {f.filename}
                  </FileButton>
                ))}
              </FlexWrapper>
            ) : (
              t('nd')
            ),
          }
        : undefined,
    ],
    [
      t,
      number,
      type,
      issueDate,
      issuedBy,
      language,
      expirationDate,
      country,
      files,
      isTaxCode,
      isMobile,
    ]
  );

  return (
    <Card style={{ position: 'relative', ...style }}>
      {action && <ActionWrapper>{action}</ActionWrapper>}
      {name && (
        <Heading margin="none" type={4}>
          {name}
        </Heading>
      )}
      <DetailsList
        cellStyle={
          isMobile
            ? {
                height: 40,
                paddingTop: SPACINGS[2],
                paddingBottom: SPACINGS[2],
              }
            : undefined
        }
        items={items}
      />
      {isMobile && (
        <>
          <Divider spacing={SPACINGS[2]} style={{ paddingTop: 0 }} />
          <Text type="label" size="sm">
            {t('files')}
          </Text>
          <FlexWrapper direction="column">
            {files?.map((f, i) => (
              <FlexWrapper
                key={i}
                align="center"
                style={{ marginBottom: SPACINGS[2] }}
              >
                <Text size="sm" margin="none">
                  {f.filename}
                </Text>
                <IconWrapper type="mobile" onClick={f.action}>
                  <IconEye stroke={theme.colors.primary} />
                </IconWrapper>
              </FlexWrapper>
            ))}
          </FlexWrapper>
        </>
      )}
    </Card>
  );
}

export default UserDocument;
